import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  MessageCircle,
  Camera,
  Send,
  MoreHorizontal,
  Pencil,
  Trash2,
  Smartphone,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useChannels } from "@/hooks/use-channels";
import { channelsApi } from "@/api/channels";
import { ChannelForm } from "@/components/channels/channel-form";
import { WhatsAppConnectDialog } from "@/components/channels/whatsapp-connect-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { StatusBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardAction,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { Channel } from "@/types/channel";
import { toast } from "sonner";

function getChannelIcon(type: Channel["type"]) {
  switch (type) {
    case "whatsapp_unofficial":
    case "whatsapp_business":
      return {
        icon: MessageCircle,
        color: "text-green-600",
        bg: "bg-green-100",
      };
    case "instagram":
      return { icon: Camera, color: "text-pink-500", bg: "bg-pink-100" };
    case "telegram":
      return { icon: Send, color: "text-blue-500", bg: "bg-blue-100" };
    default:
      return { icon: MessageCircle, color: "text-gray-500", bg: "bg-gray-100" };
  }
}

function getChannelTypeLabel(type: Channel["type"]): string {
  switch (type) {
    case "whatsapp_unofficial":
      return "WhatsApp Não Oficial";
    case "whatsapp_business":
      return "WhatsApp Business";
    case "instagram":
      return "Instagram";
    case "telegram":
      return "Telegram";
    default:
      return type;
  }
}

function isWhatsApp(type: string): boolean {
  return type === "whatsapp_unofficial" || type === "whatsapp_business";
}

export function ChannelsPage() {
  const { channels, loading, refetch, createChannel, updateChannel, deleteChannel } =
    useChannels();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Channel | null>(null);
  const [deleting, setDeleting] = useState<Channel | null>(null);
  const [connectingChannel, setConnectingChannel] = useState<Channel | null>(
    null,
  );
  const [waStatuses, setWaStatuses] = useState<Record<number, boolean>>({});
  const statusCheckedRef = useRef(false);

  const fetchWhatsAppStatuses = useCallback(async (channelList: Channel[]) => {
    const waChannels = channelList.filter(
      (c) => isWhatsApp(c.type) && c.external_id,
    );
    if (!waChannels.length) return;

    const results: Record<number, boolean> = {};
    await Promise.all(
      waChannels.map(async (c) => {
        try {
          const res = await channelsApi.getWhatsAppStatus(c.id);
          results[c.id] = !!(res.data?.logged_in);
        } catch {
          results[c.id] = false;
        }
      }),
    );
    setWaStatuses(results);
  }, []);

  useEffect(() => {
    if (!loading && channels.length && !statusCheckedRef.current) {
      statusCheckedRef.current = true;
      fetchWhatsAppStatuses(channels);
    }
  }, [loading, channels, fetchWhatsAppStatuses]);

  const handleRefetch = useCallback(async () => {
    statusCheckedRef.current = false;
    await refetch();
  }, [refetch]);

  const handleCreate = async (data: Partial<Channel>) => {
    try {
      await createChannel(data);
      toast.success("Canal criado");
    } catch {
      toast.error("Erro ao criar canal");
      throw new Error("create failed");
    }
  };

  const handleUpdate = async (data: Partial<Channel>) => {
    if (!editing) return;
    try {
      await updateChannel(editing.id, data);
      toast.success("Canal atualizado");
    } catch {
      toast.error("Erro ao atualizar canal");
      throw new Error("update failed");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const id = deleting.id;
    try {
      await deleteChannel(id);
      toast.success("Canal excluido");
    } catch {
      toast.error("Erro ao excluir canal");
    }
  };

  if (loading) {
    return (
      <div className="h-full overflow-auto space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Canais</h1>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo canal
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {channels.map((channel) => {
          const { icon: Icon, color, bg } = getChannelIcon(channel.type);

          return (
            <Card key={channel.id}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg",
                      bg,
                    )}
                  >
                    <Icon className={cn("h-5 w-5", color)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="truncate">{channel.name}</CardTitle>
                    <CardDescription>
                      {getChannelTypeLabel(channel.type)}
                    </CardDescription>
                  </div>
                </div>
                <CardAction>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          setTimeout(() => {
                            setEditing(channel);
                            setFormOpen(true);
                          }, 0);
                        }}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Editar
                      </DropdownMenuItem>
                      {isWhatsApp(channel.type) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => {
                              setTimeout(() => {
                                setConnectingChannel(channel);
                              }, 0);
                            }}
                          >
                            <Smartphone className="mr-2 h-3.5 w-3.5" />
                            WhatsApp
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onSelect={() => setDeleting(channel)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardAction>
              </CardHeader>

              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {channel.identifier}
                    </span>
                  </div>
                  <StatusBadge status={channel.status} />
                  {isWhatsApp(channel.type) && channel.external_id && (
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs gap-1",
                        waStatuses[channel.id]
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200",
                      )}
                    >
                      {waStatuses[channel.id] ? (
                        <Wifi className="h-3 w-3" />
                      ) : (
                        <WifiOff className="h-3 w-3" />
                      )}
                      {waStatuses[channel.id] ? "Conectado" : "Desconectado"}
                    </Badge>
                  )}
                </div>
              </CardContent>

              {isWhatsApp(channel.type) && (
                <CardFooter>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setConnectingChannel(channel)}
                  >
                    <Smartphone className="mr-2 h-4 w-4" />
                    {channel.external_id ? "Status WhatsApp" : "Conectar WhatsApp"}
                  </Button>
                </CardFooter>
              )}
            </Card>
          );
        })}

        {/* Add channel card */}
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="flex min-h-48 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-muted-foreground/25 bg-transparent p-6 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:bg-muted/30"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Plus className="h-6 w-6" />
          </div>
          <span className="text-sm font-medium">Adicionar canal</span>
        </button>
      </div>

      <ChannelForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={editing ? handleUpdate : handleCreate}
        channel={editing}
      />

      <WhatsAppConnectDialog
        open={!!connectingChannel}
        onClose={() => setConnectingChannel(null)}
        channel={connectingChannel}
        onChannelUpdated={handleRefetch}
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
